import { useEffect, useId, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AuthPage } from "@/client/components/auth/auth-page";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { apiJson, describeError } from "@/client/lib/api";
import { useSession } from "@/client/hooks/use-session";

export const Route = createFileRoute("/setup")({
  component: Setup,
});

const MIN_PASSWORD_LENGTH = 10;

// The token is in the URL fragment, which never reaches the server's logs
function readToken(): string {
  return new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "";
}

function Setup() {
  const id = useId();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [token] = useState(readToken);
  const [email, setEmail] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isPending, setPending] = useState(false);

  useEffect(() => {
    if (!token) {
      setLinkError("This page needs the setup link from your administrator.");
      return;
    }
    apiJson<{ email: string }>("/api/setup/inspect", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then((result) => setEmail(result.email))
      .catch((err) => setLinkError(describeError(err)));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters for your password.`);
      return;
    }
    if (password !== confirm) {
      setError("The passwords don't match.");
      return;
    }
    setPending(true);
    try {
      await apiJson("/api/setup/redeem", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, name, password }),
      });
      // Drop the token from the address bar and history
      window.history.replaceState(null, "", "/setup");
      await refresh();
      await navigate({ to: "/" });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setPending(false);
    }
  };

  if (linkError) {
    return (
      <AuthPage title="Setup link unavailable" description={linkError}>
        <p className="text-sm text-muted-foreground">
          Links work once and expire after seven days. Ask the administrator for a new one, or{" "}
          <Link to="/signin" className="underline">
            sign in
          </Link>{" "}
          if you already have an account.
        </p>
      </AuthPage>
    );
  }

  if (!email) {
    return <AuthPage title="Set up your account">{null}</AuthPage>;
  }

  return (
    <AuthPage title="Set up your account" description={<>You were invited as {email}. Choose your name and password.</>}>
      <form onSubmit={handleSubmit} className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor={`${id}-name`}>Name</Label>
          <Input
            id={`${id}-name`}
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
          <p className="text-xs text-muted-foreground">Collaborators see this next to your changes.</p>
        </div>
        <input type="email" autoComplete="username" value={email} readOnly hidden />
        <div className="grid gap-2">
          <Label htmlFor={`${id}-password`}>Password</Label>
          <Input
            id={`${id}-password`}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
          <p className="text-xs text-muted-foreground">
            At least {MIN_PASSWORD_LENGTH} characters. There's no password reset, so keep it somewhere safe.
          </p>
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`${id}-confirm`}>Confirm password</Label>
          <Input
            id={`${id}-confirm`}
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <Button type="submit" disabled={isPending}>
          {isPending ? "Creating account..." : "Create account"}
        </Button>
      </form>
    </AuthPage>
  );
}
